// =====================================================================
// DuplicateConfirmModal — Phase 7D Duplicate Prevention Guardrail UI.
// =====================================================================
//
// "Possible existing family member found" confirmation modal. Surfaced
// by every add-member flow when the pre-save scan returns one or more
// matches. Pure presentation — never writes to Supabase, never mutates
// any member field. The caller wires the three actions:
//
//   • "Use existing person"  → caller connects the relationship edge to
//                              the matched member (via
//                              connectExistingUniversal). Strong + claimed
//                              matches require an extra owner/admin
//                              checkbox before the button enables.
//   • "Create anyway"        → caller proceeds with the original insert.
//                              Strong matches require a confirmation
//                              checkbox so the user explicitly opts in.
//   • "Cancel"               → caller drops the save attempt entirely.
//
// SAFETY:
//   - NEVER auto-merges. NEVER deletes. NEVER overwrites the matched
//     member's name / birthday / photo / parent_ids / spouse_id /
//     claimed_by_user_id.
//   - When the master `duplicateGuardrail` flag is OFF this modal
//     never opens — the parent component short-circuits before
//     rendering it.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, ShieldCheck, UserPlus, Users } from 'lucide-react';
import {
  describeConfidence,
  type DuplicateMatch,
} from '@/lib/duplicates/duplicateGuard';
import MemberAvatar from '@/components/familyGraph/MemberAvatar';
import {
  RELATIONSHIP_OPTIONS,
  type GraphMember,
} from '@/contexts/FamilyGraphContext';

interface Props {
  open: boolean;
  /** Trimmed name the user is attempting to save. */
  attemptedName: string;
  /** Sorted matches returned by `findDuplicateMatches`. */
  matches: DuplicateMatch[];
  /** Whether the current user is owner / admin (gates Strong + claimed). */
  isStaff?: boolean;
  /** Pick handler. The caller is responsible for the actual edge write. */
  onUseExisting: (member: GraphMember) => void;
  /** Caller proceeds with the original insert. */
  onCreateAnyway: () => void;
  /** Caller drops the save entirely. */
  onCancel: () => void;
}

const confidenceTone: Record<
  'strong' | 'likely' | 'possible',
  { badge: string; ring: string; tone: string }
> = {
  strong: {
    badge: 'bg-rose-600 text-white',
    ring: 'border-rose-300',
    tone: 'bg-rose-50',
  },
  likely: {
    badge: 'bg-amber-600 text-white',
    ring: 'border-amber-300',
    tone: 'bg-amber-50',
  },
  possible: {
    badge: 'bg-slate-500 text-white',
    ring: 'border-slate-200',
    tone: 'bg-slate-50',
  },
};

const DuplicateConfirmModal: React.FC<Props> = ({
  open,
  attemptedName,
  matches,
  isStaff = false,
  onUseExisting,
  onCreateAnyway,
  onCancel,
}) => {
  const [acknowledgedCreate, setAcknowledgedCreate] = useState(false);
  const [staffOverride, setStaffOverride] = useState(false);

  // Reset acknowledgements every time the modal re-opens with a new set of
  // matches so a previous "Yes, create anyway" check doesn't carry over to
  // a different attempted save.
  useEffect(() => {
    if (open) {
      setAcknowledgedCreate(false);
      setStaffOverride(false);
    }
  }, [open, attemptedName]);

  const top = matches[0];
  const topConfidence = top?.confidence ?? 'possible';
  const requiresStaffApproval = useMemo(() => {
    if (!top) return false;
    if (topConfidence !== 'strong') return false;
    return !!top.member.claimedByUserId;
  }, [top, topConfidence]);

  const createBlocked =
    !acknowledgedCreate &&
    (topConfidence === 'strong' || topConfidence === 'likely');
  const useExistingBlocked = requiresStaffApproval && !staffOverride;

  if (!top) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-xl">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Possible existing family member found
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <p className="text-sm text-slate-700">
            We found {matches.length === 1 ? 'a profile' : `${matches.length} profiles`}{' '}
            that look like the person you're adding (
            <strong>{attemptedName || '—'}</strong>). Pick one of the options
            below — we'll never auto-merge or delete anything.
          </p>

          <div className="space-y-2">
            {matches.map((match) => {
              const tone = confidenceTone[match.confidence];
              const m = match.member;
              const relLabel =
                RELATIONSHIP_OPTIONS.find((o) => o.value === m.relationship)
                  ?.label || 'Relative';
              const claimed = !!m.claimedByUserId;
              return (
                <div
                  key={m.id}
                  className={`rounded-xl border-2 p-3 space-y-2 ${tone.ring} ${tone.tone}`}
                >
                  <div className="flex items-start gap-3">
                    <MemberAvatar
                      name={m.name}
                      photoUrl={m.photoUrl}
                      size="sm"
                      verified={m.approvalStatus === 'approved'}
                      pending={m.approvalStatus === 'pending'}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-slate-900 truncate">
                          {m.name}
                        </span>
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${tone.badge}`}
                        >
                          {describeConfidence(match.confidence)}
                        </span>
                        {claimed && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 bg-emerald-600 text-white">
                            <ShieldCheck className="w-3 h-3" />
                            Claimed
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-600 truncate">
                        {relLabel}
                        {m.birthYear ? ` · b. ${m.birthYear}` : ''}
                        {m.city ? ` · ${m.city}` : ''}
                      </div>
                      {match.reasons.length > 0 && (
                        <ul className="mt-1.5 text-[11px] text-slate-700 list-disc pl-4 space-y-0.5">
                          {match.reasons.map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (match === top && useExistingBlocked) return;
                        onUseExisting(m);
                      }}
                      disabled={match === top && useExistingBlocked}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 shrink-0"
                    >
                      <Users className="w-3.5 h-3.5 mr-1" />
                      Use existing
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {requiresStaffApproval && (
            <div className="rounded-lg bg-rose-50 border-2 border-rose-200 p-3 space-y-2">
              <div className="flex items-start gap-2 text-xs text-rose-900">
                <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  This profile is already claimed by a signed-in family
                  member. Connecting another relative to it requires owner /
                  admin approval so we never overwrite their claimed identity.
                </span>
              </div>
              {isStaff ? (
                <label className="flex items-start gap-2 text-xs text-rose-900 font-medium cursor-pointer select-none">
                  <Checkbox
                    checked={staffOverride}
                    onCheckedChange={(v) => setStaffOverride(v === true)}
                    className="mt-0.5"
                  />
                  <span>
                    I confirm as owner / admin that connecting through this
                    claimed profile is intentional. We will only attach the new
                    relationship edge — the existing claim, name, photo,
                    birthday, parent_ids, and spouse_id stay untouched.
                  </span>
                </label>
              ) : (
                <p className="text-[11px] text-rose-800 italic">
                  Ask a family owner / admin to confirm this connection.
                </p>
              )}
            </div>
          )}

          {(topConfidence === 'strong' || topConfidence === 'likely') && (
            <label className="flex items-start gap-2 text-xs text-slate-800 cursor-pointer select-none rounded-lg bg-amber-50 border border-amber-200 p-3">
              <Checkbox
                checked={acknowledgedCreate}
                onCheckedChange={(v) => setAcknowledgedCreate(v === true)}
                className="mt-0.5"
              />
              <span>
                I understand this looks like an existing family member. I
                still want to <strong>create a separate profile</strong> for{' '}
                <strong>{attemptedName || 'this person'}</strong>. The
                existing profile will <strong>not</strong> be modified.
              </span>
            </label>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={createBlocked}
              onClick={onCreateAnyway}
              className="border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
            >
              <UserPlus className="w-3.5 h-3.5 mr-1" />
              Create anyway
            </Button>
          </div>
          <p className="text-[10px] text-slate-500 leading-snug">
            We never auto-merge or delete duplicates. The existing member's
            name, birthday, photo, parents, spouse, and account claim are
            never overwritten by this guardrail — it only chooses which
            relationship edge to attach.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateConfirmModal;
